import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';

const bounce = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const GameContainer = styled.div`
  padding: 40px 20px;
  text-align: center;
`;

const GameTitle = styled.h2`
  font-size: 2.5em;
  color: #ff6b6b;
  text-shadow: 3px 3px 6px rgba(0, 0, 0, 0.3);
  margin-bottom: 20px;
  animation: ${bounce} 2s ease-in-out infinite;
  font-family: 'Comic Sans MS', cursive, sans-serif;
`;

const StartButton = styled.button`
  padding: 15px 40px;
  font-size: 1.3em;
  background: linear-gradient(135deg, #ff6b6b, #ee5a6f);
  color: white;
  border: none;
  border-radius: 30px;
  cursor: pointer;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  transition: all 0.3s;
  font-family: 'Comic Sans MS', cursive, sans-serif;
  font-weight: bold;

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 7px 20px rgba(0, 0, 0, 0.4);
  }
`;

const GameStats = styled.div`
  display: flex;
  justify-content: center;
  gap: 30px;
  flex-wrap: wrap;
  margin: 20px 0;
  font-size: 1.3em;
  font-weight: bold;
  color: white;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
`;

const MatchingBoard = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  max-width: 800px;
  margin: 30px auto;
  padding: 20px;

  @media (max-width: 768px) {
    gap: 20px;
  }
`;

const WordColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const ColumnTitle = styled.h3`
  font-size: 1.5em;
  color: white;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
  margin-bottom: 10px;
  font-family: 'Comic Sans MS', cursive, sans-serif;
`;

const WordCard = styled.div`
  padding: 20px;
  background: ${props => {
    if (props.matched) return 'linear-gradient(135deg, #2ecc71, #27ae60)';
    if (props.selected) return 'linear-gradient(135deg, #3498db, #2980b9)';
    return 'linear-gradient(135deg, #fff, #f0f0f0)';
  }};
  border: 3px solid ${props => props.selected ? '#2980b9' : props.matched ? '#27ae60' : '#ddd'};
  border-radius: 15px;
  cursor: ${props => props.matched ? 'default' : 'pointer'};
  font-size: 1.2em;
  font-weight: bold;
  color: ${props => props.matched || props.selected ? 'white' : '#333'};
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  transition: all 0.3s;
  font-family: 'Comic Sans MS', cursive, sans-serif;
  opacity: ${props => props.matched ? 0.7 : 1};
  animation: ${props => props.matched ? pulse : 'none'} 0.5s ease-out;

  &:hover {
    transform: ${props => props.matched ? 'none' : 'scale(1.05)'};
    box-shadow: ${props => props.matched ? '0 5px 15px rgba(0, 0, 0, 0.2)' : '0 7px 20px rgba(0, 0, 0, 0.3)'};
  }

  @media (max-width: 768px) {
    padding: 15px;
    font-size: 1em;
  }
`;

const Message = styled.div`
  font-size: 1.3em;
  color: white;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
  margin: 20px 0;
  font-weight: bold;
`;

const GameComplete = styled.div`
  padding: 30px;
  background: rgba(0, 0, 0, 0.8);
  border-radius: 20px;
  color: white;
  max-width: 400px;
  margin: 0 auto;

  h3 {
    font-size: 2em;
    margin-bottom: 15px;
    color: #2ecc71;
  }

  p {
    font-size: 1.2em;
    margin: 10px 0;
  }
`;

const Instructions = styled.p`
  font-size: 1.1em;
  color: white;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
  margin: 20px 0;
`;

function MatchingGame() {
  const [isActive, setIsActive] = useState(false);
  const [gameComplete, setGameComplete] = useState(false);
  const [score, setScore] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const [selectedDutch, setSelectedDutch] = useState(null);
  const [selectedDanish, setSelectedDanish] = useState(null);
  const [matched, setMatched] = useState([]);
  const [message, setMessage] = useState('');

  // Move wordPairs outside component or use useMemo to prevent recreation
  const wordPairs = React.useMemo(() => [
    { dutch: 'kat', danish: 'kat', id: 1 },
    { dutch: 'wijn', danish: 'vin', id: 2 },
    { dutch: 'rood', danish: 'rød', id: 3 },
    { dutch: 'wit', danish: 'hvid', id: 4 },
    { dutch: 'poes', danish: 'killing', id: 5 },
    { dutch: 'glas', danish: 'glas', id: 6 },
    { dutch: 'fles', danish: 'flaske', id: 7 },
    { dutch: 'proost', danish: 'skål', id: 8 },
    { dutch: 'droog', danish: 'tør', id: 9 },
    { dutch: 'zoet', danish: 'sød', id: 10 },
    { dutch: 'druif', danish: 'drue', id: 11 },
    { dutch: 'kelder', danish: 'kælder', id: 12 },
  ], []);

  const [dutchWords, setDutchWords] = useState([]);
  const [danishWords, setDanishWords] = useState([]);

  useEffect(() => {
    if (isActive) {
      // Shuffle words
      const shuffledDutch = [...wordPairs].sort(() => Math.random() - 0.5);
      const shuffledDanish = [...wordPairs].sort(() => Math.random() - 0.5);
      setDutchWords(shuffledDutch);
      setDanishWords(shuffledDanish);
      setMatched([]);
      setScore(0);
      setAttempts(0);
      setMessage('Match the Dutch and Danish words!');
    }
  }, [isActive]);

  useEffect(() => {
    if (selectedDutch !== null && selectedDanish !== null) {
      setAttempts(prev => prev + 1);
      
      if (selectedDutch === selectedDanish) {
        // Match!
        const newMatched = [...matched, selectedDutch];
        setMatched(newMatched);
        setScore(prev => prev + 10);
        setMessage('🎉 Perfect match! Uitstekend! Fremragende!');
        
        if (newMatched.length === wordPairs.length) {
          setTimeout(() => {
            setGameComplete(true);
            setIsActive(false);
          }, 1000);
        }
      } else {
        // No match
        setMessage('❌ Not a match! Try again!');
      }
      
      setTimeout(() => {
        setSelectedDutch(null);
        setSelectedDanish(null);
      }, 1500);
    }
  }, [selectedDutch, selectedDanish]);

  const handleDutchClick = (id) => {
    if (matched.includes(id) || selectedDutch !== null) return;
    setSelectedDutch(id);
  };

  const handleDanishClick = (id) => {
    if (matched.includes(id) || selectedDanish !== null) return;
    setSelectedDanish(id);
  };

  const startGame = () => {
    setIsActive(true);
    setGameComplete(false);
  };

  return (
    <GameContainer id="matching-game">
      <GameTitle>🇳🇱 DUTCH-DANISH MATCH 🇩🇰</GameTitle>
      
      {!isActive && !gameComplete && (
        <>
          <Instructions>
            Match the Dutch words with their Danish translations!<br />
            Wine & Cat themed vocabulary! 🍷😺
          </Instructions>
          <StartButton onClick={startGame}>START MATCHING!</StartButton>
        </>
      )}

      {isActive && (
        <>
          <GameStats>
            <span>✨ Score: {score}</span>
            <span>🎯 Attempts: {attempts}</span>
            <span>✅ Matched: {matched.length}/{wordPairs.length}</span>
          </GameStats>
          <Message>{message}</Message>
          
          <MatchingBoard>
            <WordColumn>
              <ColumnTitle>🇳🇱 Nederlands</ColumnTitle>
              {dutchWords.map((word) => (
                <WordCard
                  key={word.id}
                  selected={selectedDutch === word.id}
                  matched={matched.includes(word.id)}
                  onClick={() => handleDutchClick(word.id)}
                >
                  {word.dutch}
                </WordCard>
              ))}
            </WordColumn>
            
            <WordColumn>
              <ColumnTitle>🇩🇰 Dansk</ColumnTitle>
              {danishWords.map((word) => (
                <WordCard
                  key={word.id}
                  selected={selectedDanish === word.id}
                  matched={matched.includes(word.id)}
                  onClick={() => handleDanishClick(word.id)}
                >
                  {word.danish}
                </WordCard>
              ))}
            </WordColumn>
          </MatchingBoard>
        </>
      )}

      {gameComplete && (
        <GameComplete>
          <h3>🏆 GEFELICITEERD! TILLYKKE! 🏆</h3>
          <p>You matched all the words!</p>
          <p>Score: {score}</p>
          <p>Attempts: {attempts}</p>
          <p>Accuracy: {Math.round((wordPairs.length / attempts) * 100)}%</p>
          <StartButton onClick={startGame} style={{ marginTop: '20px' }}>
            PLAY AGAIN!
          </StartButton>
        </GameComplete>
      )}
    </GameContainer>
  );
}

export default MatchingGame;
